import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  Typography,
  IconButton,
  Paper,
  Box
} from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  Close as CloseIcon,
  MonetizationOn,
  Assignment,
  Loyalty
} from "@material-ui/icons";
import { Formik, Form, Field } from "formik";
import ButtonWithSpinner from "../ButtonWithSpinner";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    maxWidth: "820px !important",
    width: "100%",
    backgroundColor: theme.palette.type === "dark" ? "#16191E" : "#FFFFFF",
    border: theme.palette.type === "dark" ? "1px solid rgba(0, 229, 255, 0.25)" : "1px solid rgba(0, 0, 0, 0.12)",
    borderRadius: 14,
    color: theme.palette.type === "dark" ? "#FFFFFF" : "#1E293B",
    boxShadow: "0 20px 50px rgba(0, 0, 0, 0.8)",
  },
  dialogTitle: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderBottom: theme.palette.type === "dark" ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid rgba(0, 0, 0, 0.08)",
    padding: theme.spacing(2, 3),
  },
  titleText: {
    fontSize: "1.1rem",
    fontWeight: 700,
    color: theme.palette.primary.main,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  formRoot: {
    display: "flex",
    flexDirection: "column",
    maxHeight: "85vh",
    overflow: "hidden",
  },
  dialogContent: {
    padding: theme.spacing(2.5, 3),
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  sectionCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    borderRadius: 10,
    backgroundColor: theme.palette.type === "dark" ? "#111418" : "#F8FAFC",
    border: theme.palette.type === "dark" ? "1px solid rgba(255, 255, 255, 0.06)" : "1px solid rgba(0, 0, 0, 0.06)",
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
    borderBottom: theme.palette.type === "dark" ? "1px solid rgba(255, 255, 255, 0.05)" : "1px solid rgba(0, 0, 0, 0.05)",
    paddingBottom: theme.spacing(1),
  },
  sectionTitle: {
    fontSize: "0.85rem",
    fontWeight: 700,
    letterSpacing: "0.5px",
    color: theme.palette.primary.main,
    textTransform: "uppercase",
  },
  sectionIcon: {
    color: theme.palette.primary.main,
    fontSize: "1.2rem",
  },
  dialogActions: {
    padding: theme.spacing(2, 3),
    borderTop: theme.palette.type === "dark" ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid rgba(0, 0, 0, 0.08)",
    gap: theme.spacing(1.5),
  },
  actionButton: {
    borderRadius: 8,
    fontWeight: 700,
    fontSize: "0.88rem",
    textTransform: "none",
    padding: "8px 26px",
    backgroundColor: `${theme.palette.primary.main} !important`,
    color: `${theme.palette.primary.contrastText || "#ffffff"} !important`,
    boxShadow: `0 0 14px ${theme.palette.primary.main}66`,
    transition: "all 0.25s ease",
    "&:hover": {
      backgroundColor: `${theme.palette.primary.main}CC !important`,
      boxShadow: `0 0 18px ${theme.palette.primary.main}99`,
    },
    "&:disabled": {
      backgroundColor: "rgba(255, 255, 255, 0.12) !important",
      color: "rgba(255, 255, 255, 0.3) !important",
      boxShadow: "none",
    },
  },
  outlineButton: {
    borderColor: `${theme.palette.primary.main}80`,
    color: theme.palette.primary.main,
    fontWeight: 600,
    borderRadius: 8,
    textTransform: "none",
    padding: "6px 20px",
    fontSize: "0.88rem",
    transition: "all 0.25s ease",
    "&:hover": {
      borderColor: theme.palette.primary.main,
      backgroundColor: `${theme.palette.primary.main}14`,
      boxShadow: "0 0 10px rgba(0, 229, 255, 0.2)",
    },
  },
  deleteButton: {
    borderColor: "#ff5252",
    color: "#ff5252",
    fontWeight: 600,
    borderRadius: 8,
    textTransform: "none",
    padding: "6px 16px",
    fontSize: "0.88rem",
    marginRight: "auto",
    "&:hover": {
      borderColor: "#ff1744",
      backgroundColor: "rgba(255, 82, 82, 0.08)",
      boxShadow: "0 0 10px rgba(255, 82, 82, 0.2)",
    },
  },
}));

export default function ResellerPlanModal({
  open,
  onClose,
  initialValue,
  onSubmit,
  onDelete,
  loading,
  currency = "$"
}) {
  const classes = useStyles();
  const theme = useTheme();

  const [record, setRecord] = useState({
    name: "",
    value: 0,
    discountPercentage: 0,
    earnedBalance: 0,
    expiresIn: 365
  });

  useEffect(() => {
    if (!initialValue) return;
    setRecord({
      ...initialValue,
      expiresIn: initialValue.expiresIn || 365
    });
  }, [initialValue]);

  const isEditing = Boolean(record && record.id);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ className: classes.dialogPaper }}
    >
      <div className={classes.dialogTitle}>
        <Typography className={classes.titleText}>
          <Loyalty style={{ color: theme.palette.primary.main, fontSize: "1.4rem" }} />
          {isEditing
            ? i18n.t("resellerPlans.modal.titleEdit", { name: record.name })
            : i18n.t("resellerPlans.modal.titleNew")}
        </Typography>
        <IconButton size="small" onClick={onClose} style={{ color: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.7)" : "#64748B" }}>
          <CloseIcon />
        </IconButton>
      </div>

      <Formik
        enableReinitialize
        initialValues={record}
        onSubmit={async (values) => {
          await onSubmit(values);
        }}
      >
        {({ values, setFieldValue }) => (
          <Form className={classes.formRoot}>
            <DialogContent className={classes.dialogContent} dividers>
              {/* 1. INFORMACIÓN GENERAL */}
              <Paper className={classes.sectionCard} elevation={0}>
                <div className={classes.sectionHeader}>
                  <Assignment className={classes.sectionIcon} />
                  <Typography className={classes.sectionTitle}>
                    {i18n.t("resellerPlans.sections.general")}
                  </Typography>
                </div>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Field
                      as={TextField}
                      label={i18n.t("resellerPlans.form.name")}
                      name="name"
                      variant="outlined"
                      size="small"
                      fullWidth
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Field
                      as={TextField}
                      label={i18n.t("resellerPlans.form.expiresIn")}
                      name="expiresIn"
                      variant="outlined"
                      size="small"
                      type="number"
                      disabled={true}
                      fullWidth
                      helperText="365 días (1 año de vigencia estándar)"
                    />
                  </Grid>
                </Grid>
              </Paper>

              {/* 2. CONDICIONES COMERCIALES Y DESCUENTO */}
              <Paper className={classes.sectionCard} elevation={0}>
                <div className={classes.sectionHeader}>
                  <MonetizationOn className={classes.sectionIcon} />
                  <Typography className={classes.sectionTitle}>
                    {i18n.t("resellerPlans.sections.balance")}
                  </Typography>
                </div>
                <Grid container spacing={2}>
                  {/* VALOR */}
                  <Grid item xs={12} sm={4}>
                    <Field name="value">
                      {({ field }) => (
                        <TextField
                          {...field}
                          label={`${i18n.t("resellerPlans.form.value")} (${currency})`}
                          variant="outlined"
                          size="small"
                          fullWidth
                          type="number"
                          inputProps={{ step: "0.01", min: "0" }}
                          required
                          onChange={(e) => {
                            const val = parseFloat(e.target.value) || 0;
                            const disc = parseFloat(values.discountPercentage) || 0;
                            setFieldValue("value", e.target.value);
                            if (disc > 0 && disc < 100) {
                              const earned = parseFloat((val / (1 - disc / 100)).toFixed(2));
                              setFieldValue("earnedBalance", earned);
                            } else {
                              setFieldValue("earnedBalance", val);
                            }
                          }}
                        />
                      )}
                    </Field>
                  </Grid>

                  {/* DESCUENTO */}
                  <Grid item xs={12} sm={4}>
                    <Field name="discountPercentage">
                      {({ field }) => (
                        <TextField
                          {...field}
                          label={i18n.t("resellerPlans.form.discountPercentage")}
                          variant="outlined"
                          size="small"
                          fullWidth
                          type="number"
                          inputProps={{ step: "0.1", min: "0", max: "100" }}
                          onChange={(e) => {
                            const disc = parseFloat(e.target.value) || 0;
                            const val = parseFloat(values.value) || 0;
                            setFieldValue("discountPercentage", e.target.value);
                            if (disc > 0 && disc < 100) {
                              const earned = parseFloat((val / (1 - disc / 100)).toFixed(2));
                              setFieldValue("earnedBalance", earned);
                            } else {
                              setFieldValue("earnedBalance", val);
                            }
                          }}
                        />
                      )}
                    </Field>
                  </Grid>

                  {/* SALDO COMERCIAL */}
                  <Grid item xs={12} sm={4}>
                    <Field name="earnedBalance">
                      {({ field }) => (
                        <TextField
                          {...field}
                          label={`${i18n.t("resellerPlans.form.earnedBalance")} (${currency})`}
                          variant="outlined"
                          size="small"
                          fullWidth
                          type="number"
                          inputProps={{ step: "0.01" }}
                          helperText={i18n.t("resellerPlans.toasts.formulaHelp", "= Precio ÷ (1 - %)")}
                        />
                      )}
                    </Field>
                  </Grid>
                </Grid>
              </Paper>
            </DialogContent>

            <DialogActions className={classes.dialogActions}>
              {isEditing && (
                <ButtonWithSpinner
                  className={classes.deleteButton}
                  variant="outlined"
                  onClick={() => onDelete(record)}
                  loading={loading}
                >
                  {i18n.t("resellerPlans.buttons.delete")}
                </ButtonWithSpinner>
              )}
              <ButtonWithSpinner
                className={classes.outlineButton}
                variant="outlined"
                onClick={onClose}
                disabled={loading}
              >
                {i18n.t("resellerPlans.buttons.cancel")}
              </ButtonWithSpinner>
              <ButtonWithSpinner
                className={classes.actionButton}
                type="submit"
                loading={loading}
              >
                {i18n.t("resellerPlans.buttons.save")}
              </ButtonWithSpinner>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
}
